import { createContext, useCallback, useReducer } from 'react'
import { format } from 'date-fns'

import axios from 'utils/axios'
import isEmptyObject from 'helpers/node/isEmptyObject'
import { DELIVERY_TYPES } from 'utils/constants'

const SET_STAGE = 'SET_STAGE'
const SET_SHIPPING_ADDRESS = 'SET_SHIPPING_ADDRESS'
const SET_GENERAL_INFORMATION = 'SET_GENERAL_INFORMATION'
const SET_POSTING_NOTICES = 'SET_POSTING_NOTICES'
const SET_COVER_LETTER = 'SET_COVER_LETTER'
const SET_SORTING_OPTION = 'SET_SORTING_OPTION'
const SET_MAIL_TYPE = 'SET_MAIL_TYPE'
const RESET = 'RESET'

const generalInformationRenderingData = [
  {
    title: 'Named Insured',
    fields: [
      {
        label: 'Named Insured',
        name: 'ni_name',
        type: 'text',
        cols: 12,
        required: true,
      },
      {
        label: 'Primary Address',
        name: 'ni_address',
        type: 'address',
      },
      {
        label: 'Phone',
        name: 'ni_phone',
        type: 'text',
      },
      {
        label: 'FEIN',
        name: 'ni_fein',
        type: 'text',
        required: true,
        cols: 12,
      },
    ],
  },
  {
    title: 'Policy Information',
    fields: [
      {
        label: 'Effective Date',
        name: 'pi_effective_date',
        type: 'date',
        required: true,
      },
      {
        label: 'Expiration Date',
        name: 'pi_expiration_date',
        type: 'date',
        required: true,
      },
      {
        label: 'Policy Number',
        name: 'pi_policy_number',
        type: 'text',
        required: true,
      },
      {
        label: 'Issuing Insurance Company',
        name: 'pi_issuing_insurance_company',
        type: 'text',
        required: true,
      },
      {
        label: 'Issuing Company Mailing Address',
        name: 'pi_issuing_company_mailing_address',
        type: 'address',
      },
      {
        label: 'Issuing Company Phone',
        name: 'pi_issuing_company_phone',
        type: 'text',
      },
    ],
  },
  {
    title: 'Claims Information',
    fields: [
      {
        label: 'Claims Administrator Name',
        name: 'ci_administrator_name',
        type: 'text',
        cols: 12,
        required: true,
      },
      {
        label: 'Claims Mailing Address',
        name: 'ci_mailing_address',
        type: 'address',
      },
      {
        label: 'Claims Administrator Phone ',
        name: 'ci_phone',
        type: 'text',
      },
    ],
  },
  {
    title: 'MCO/MPN Information',
    fields: [
      {
        label: 'Company Name',
        name: 'mco_company_name',
        type: 'text',
      },
      {
        label: 'Phone',
        name: 'mco_phone',
        type: 'text',
      },
      {
        label: 'Email',
        name: 'mco_email',
        type: 'text',
        cols: 12,
      },
    ],
  },
  {
    title: 'Broker/Agent Information',
    fields: [
      {
        label: 'Producer Name',
        name: 'ba_producer_name',
        type: 'text',
        cols: 12,
      },
      {
        label: 'Mailing Address',
        name: 'ba_mailing_address',
        type: 'address',
      },
      {
        label: 'Phone',
        name: 'ba_phone',
        type: 'text',
      },
    ],
  },
  {
    title: 'Authorized Representative',
    fields: [
      {
        label: 'Employer Authorized Representative',
        name: 'ar_name',
        type: 'text',
        cols: 12,
      },
      {
        label: 'Authorized Representative Address',
        name: 'ar_address',
        type: 'address',
      },
      {
        label: 'Authorized Representative Phone',
        name: 'ar_phone',
        type: 'text',
      },
    ],
  },
]

const generalInformationAddressMapping = {
  ni_address: {
    address: {
      key: 'ni_address',
      label: 'Primary Address',
    },
    city: {
      key: 'ni_city',
      label: 'City',
    },
    state: {
      key: 'ni_state',
      label: 'State',
    },
    zip: {
      key: 'ni_zip',
      label: 'Zip',
    },
  },
  pi_issuing_company_mailing_address: {
    address: {
      key: 'pi_issuing_company_mailing_address',
      label: 'Issuing Company Mailing Address',
    },
    city: {
      key: 'pi_issuing_company_city',
      label: 'Issuing Company City',
    },
    state: {
      key: 'pi_issuing_company_state',
      label: 'Issuing Company State',
    },
    zip: {
      key: 'pi_issuing_company_zip',
      label: 'Issuing Company Zip',
    },
  },
  ci_mailing_address: {
    address: {
      key: 'ci_mailing_address',
      label: 'Claims Mailing Address',
    },
    city: {
      key: 'ci_city',
      label: 'Claims City',
    },
    state: {
      key: 'ci_state',
      label: 'Claims State',
    },
    zip: {
      key: 'ci_zip',
      label: 'Claims Zip',
    },
  },
  ba_mailing_address: {
    address: {
      key: 'ba_mailing_address',
      label: 'Mailing Address',
    },
    city: {
      key: 'ba_city',
      label: 'City',
    },
    state: {
      key: 'ba_state',
      label: 'State',
    },
    zip: {
      key: 'ba_zip',
      label: 'Zip',
    },
  },
  ar_address: {
    address: {
      key: 'ar_address',
      label: 'Authorized Representative Address',
    },
    city: {
      key: 'ar_city',
      label: 'Authorized Representative City',
    },
    state: {
      key: 'ar_state',
      label: 'Authorized Representative State',
    },
    zip: {
      key: 'ar_zip',
      label: 'Authorized Representative Zip',
    },
  },
}

const initialState = {
  stage: 0,
  mailType: DELIVERY_TYPES.MAIL,
  shippingAddress: {},
  generalInformation: {},
  postingNotices: [
    {
      state: '',
      quantity: 0,
    },
  ],
  coverLetter: {
    coverLetter: null,
    attachments: [],
  },
  sortingOption: '',
  generalInformationRenderingData,
  generalInformationAddressMapping,
}

const PostingNoticeRequestContext = createContext(null)

const PostingNoticeRequestReducer = (state, action) => {
  switch (action.type) {
    case SET_STAGE:
      return {
        ...state,
        stage: action.stage,
      }
    case SET_SHIPPING_ADDRESS:
      return {
        ...state,
        shippingAddress: action.shippingAddress,
      }
    case SET_GENERAL_INFORMATION:
      return {
        ...state,
        generalInformation: action.generalInformation,
      }
    case SET_POSTING_NOTICES:
      return {
        ...state,
        postingNotices: action.postingNotices,
      }
    case SET_COVER_LETTER:
      return {
        ...state,
        coverLetter: action.coverLetter,
      }
    case SET_SORTING_OPTION:
      return {
        ...state,
        sortingOption: action.sortingOption,
      }
    case SET_MAIL_TYPE:
      return {
        ...state,
        mailType: action.mailType,
      }
    case RESET:
      return {
        ...initialState,
      }
    default:
      return state
  }
}

function PostingNoticeRequestProvider({ children }) {
  const [state, dispatch] = useReducer(
    PostingNoticeRequestReducer,
    initialState
  )

  const createPostingNoticeRequest = useCallback(async () => {
    const formData = new FormData()

    const generalInformation = {
      ...state.generalInformation,
      pi_effective_date: format(
        state.generalInformation.pi_effective_date,
        'yyyy-MM-dd'
      ),
      pi_expiration_date: format(
        state.generalInformation.pi_expiration_date,
        'yyyy-MM-dd'
      ),
    }

    formData.append('is_email', state.mailType === DELIVERY_TYPES.EMAIL ? 1 : 0)
    formData.append('is_mail', state.mailType === DELIVERY_TYPES.MAIL ? 1 : 0)

    const files = {}

    if (state.coverLetter.coverLetter) {
      files.cover_letter = {
        filename: state.coverLetter.coverLetter.name,
      }
    }

    if (state.coverLetter.attachments) {
      state.coverLetter.attachments.forEach((attachment, index) => {
        files[`fileupload${index + 1}`] = {
          filename: attachment.name,
        }
      })
    }

    if (!isEmptyObject(files)) {
      formData.append('files', JSON.stringify(files))
    }

    formData.append(
      'posting_notice_request',
      JSON.stringify({
        ...state.shippingAddress,
        ...generalInformation,
        posting_notices: state.postingNotices,
        sorting_option: state.sortingOption,
      })
    )

    const response = await axios.post('/posting-notice-requests', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response.data
  }, [state])

  const submitPostingNoticeRequest = useCallback(
    async (postingNoticeRequestId) => {
      const formData = new FormData()

      if (state.coverLetter.coverLetter) {
        formData.append('cover_letter', state.coverLetter.coverLetter)
      }

      if (state.coverLetter.attachments) {
        state.coverLetter.attachments.forEach((attachment, index) => {
          formData.append(`fileupload${index + 1}`, attachment)
        })
      }

      const response = await axios.post(
        `/posting-notice-requests/${postingNoticeRequestId}/submissions`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return response.data
    },
    [state]
  )

  const downloadPostingNoticeRequest = useCallback(
    async (postingNoticeRequestId) => {
      const formData = new FormData()

      if (state.coverLetter.coverLetter) {
        formData.append('cover_letter', state.coverLetter.coverLetter)
      }

      if (state.coverLetter.attachments) {
        state.coverLetter.attachments.forEach((attachment, index) => {
          formData.append(`fileupload${index + 1}`, attachment)
        })
      }

      const response = await axios.post(
        `/posting-notice-requests/${postingNoticeRequestId}/previews`,
        formData,
        {
          responseType: 'blob',
          headers: {
            Accept: 'application/pdf',
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return response.data
    },
    [state]
  )

  const getPostingNoticeRequest = useCallback(
    async (postingNoticeRequestId) => {
      const response = await axios.get(
        `/posting-notice-requests/${postingNoticeRequestId}`
      )
      return response.data
    },
    []
  )

  const updatePostingNoticeRequest = useCallback(
    async (postingNoticeRequestId) => {
      const res = await axios.get(
        `/posting-notice-requests/${postingNoticeRequestId}`
      )
      const { etag } = res.headers

      const response = await axios.patch(
        `/posting-notice-requests/${postingNoticeRequestId}`,
        {
          is_email: state.mailType === DELIVERY_TYPES.EMAIL ? 1 : 0,
          is_mail: state.mailType === DELIVERY_TYPES.MAIL ? 1 : 0,
          posting_notice_request: {
            ...state.shippingAddress,
          },
        },
        {
          headers: {
            'If-Match': etag,
          },
        }
      )
      return response.data
    },
    [state]
  )

  return (
    <PostingNoticeRequestContext.Provider
      value={{
        ...state,
        setStage: (stage) => dispatch({ type: SET_STAGE, stage }),
        setShippingAddress: (shippingAddress) =>
          dispatch({
            type: SET_SHIPPING_ADDRESS,
            shippingAddress:
              state.mailType === DELIVERY_TYPES.EMAIL
                ? {
                    name: shippingAddress.name ?? '',
                    email: shippingAddress.email ?? '',
                  }
                : {
                    company_name: shippingAddress.company_name ?? '',
                    name: shippingAddress.name ?? '',
                    address_line_1: shippingAddress.address_line_1 ?? '',
                    address_line_2: shippingAddress.address_line_2 ?? '',
                    address_line_3: shippingAddress.address_line_3 ?? '',
                    address_line_4: shippingAddress.address_line_4 ?? '',
                    city: shippingAddress.city ?? '',
                    state: shippingAddress.state ?? '',
                    zip_code: shippingAddress.zip_code ?? '',
                    country: 'USA',
                  },
          }),
        setGeneralInformation: (generalInformation) =>
          dispatch({ type: SET_GENERAL_INFORMATION, generalInformation }),
        setPostingNotices: (postingNotices) =>
          dispatch({ type: SET_POSTING_NOTICES, postingNotices }),
        setCoverLetter: (coverLetter) =>
          dispatch({ type: SET_COVER_LETTER, coverLetter }),
        setSortingOption: (sortingOption) =>
          dispatch({ type: SET_SORTING_OPTION, sortingOption }),
        setMailType: (mailType) => dispatch({ type: SET_MAIL_TYPE, mailType }),
        reset: () => dispatch({ type: RESET }),
        createPostingNoticeRequest,
        submitPostingNoticeRequest,
        downloadPostingNoticeRequest,
        getPostingNoticeRequest,
        updatePostingNoticeRequest,
      }}
    >
      {children}
    </PostingNoticeRequestContext.Provider>
  )
}

export { PostingNoticeRequestContext, PostingNoticeRequestProvider }
